import { toast } from 'sonner'
import { useMemo, useState } from 'react'

import { StorePersistKeys } from 'src/types'
import { useLocales } from 'src/contexts'
import { KEYS_FOR_INVALIDATION } from 'src/constants'

import { useManageUserCars } from 'src/hooks/useManageUserCars'
import { useGetAllMaintenanceByCarNumber } from 'src/hooks/useGetAllMaintenanceByCarNumber'
import { formattedDateByLang } from 'src/helpers/formatDateByLang'

import { getLastMaintenanceInfo } from 'src/features/CarCard/helpers'
import { EMPTY_INFO } from 'src/features/CarCard/constants'
import { queryClient } from 'src/features/AppProviders/constants'

export const useCarCard = (carNumber: string, isActive: boolean) => {
  const { translations, locale } = useLocales()

  const { setActiveCar, deleteCar } = useManageUserCars()

  const [isEditMode, setEditMode] = useState(false)

  const { data: historyOfMaintenance } =
    useGetAllMaintenanceByCarNumber(carNumber)

  const formatValue = (value?: string | number) => {
    if (!value) return EMPTY_INFO

    return value.toString()
  }

  const handleFormatDate = (seconds?: number): string => {
    if (!seconds) {
      return EMPTY_INFO
    }

    return formattedDateByLang(seconds, locale)
  }

  const handleSetActiveCar = (): void => {
    const selection = window.getSelection()

    if (selection && selection.toString().length > 0) {
      return
    }

    if (isActive) return

    setActiveCar(carNumber, {
      onSuccess: () => {
        KEYS_FOR_INVALIDATION.forEach(key => {
          queryClient.invalidateQueries({ queryKey: [key] })
        })
      },
      onError: error => {
        toast(`${error}`)
      },
    })
  }

  const handleEdit = () => {
    setEditMode(prevEditMode => !prevEditMode)
  }

  const handleDelete = () => {
    deleteCar(carNumber, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [StorePersistKeys.USER_CARS],
        })

        toast(translations.trackFuel.historyFuel.toast.success.title, {
          description:
            translations.trackFuel.historyFuel.toast.success.description,
          action: {
            label:
              translations.trackFuel.historyFuel.toast.success.action.label,
            onClick: () => toast.dismiss(),
          },
        })
      },
      onError: error => {
        toast(`${error}`)
      },
    })
  }

  const lastMaintenanceInfo = useMemo(() => {
    return getLastMaintenanceInfo(
      translations.maintenance,
      historyOfMaintenance,
    )
  }, [historyOfMaintenance])

  return {
    locale: translations.myCars.fields,
    formatValue,
    handleFormatDate,
    handleSetActiveCar,
    handleEdit,
    isEditMode,
    handleDelete,
    lastMaintenanceInfo,
  }
}
